import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { GripVertical, X, Search, ExternalLink, Plus, Info } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { TaskTemplateFormModal } from "./task-template-form-modal";
import type { ServiceType, TaskTemplate } from "@shared/schema";

interface ServiceTemplateWithItems {
  id: number;
  serviceTypeId: string;
  serviceType: ServiceType;
  items: Array<{
    id: number;
    sequenceNumber: number;
    taskTemplate: TaskTemplate;
  }>;
}

const serviceTemplateFormSchema = z.object({
  serviceTypeId: z.string().min(1, "Service type is required"),
});

type ServiceTemplateFormData = z.infer<typeof serviceTemplateFormSchema>;

interface ServiceTemplateFormModalProps {
  open: boolean;
  onClose: () => void;
  template?: ServiceTemplateWithItems | null;
}

export function ServiceTemplateFormModal({ open, onClose, template }: ServiceTemplateFormModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!template;
  
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [draggedTaskId, setDraggedTaskId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);

  const form = useForm<ServiceTemplateFormData>({
    resolver: zodResolver(serviceTemplateFormSchema),
    defaultValues: {
      serviceTypeId: "",
    },
  });

  // Fetch service types
  const { data: serviceTypes = [] } = useQuery<ServiceType[]>({
    queryKey: ['/api/service-types']
  });

  // Fetch all task templates (only active ones for selection)
  const { data: taskTemplates = [] } = useQuery<TaskTemplate[]>({
    queryKey: ['/api/task-templates'],
    select: (data) => data.filter(t => t.isActive),
  });

  // Fetch existing service templates to disable already-used service types
  const { data: existingTemplates = [] } = useQuery<ServiceTemplateWithItems[]>({
    queryKey: ['/api/service-templates']
  });

  useEffect(() => {
    if (template) {
      form.reset({
        serviceTypeId: template.serviceTypeId,
      });
      setSelectedTaskIds(
        template.items
          .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
          .map(item => item.taskTemplate.id)
      );
    } else {
      form.reset({
        serviceTypeId: "",
      });
      setSelectedTaskIds([]);
    }
  }, [template, form]);

  const createMutation = useMutation({
    mutationFn: (data: { serviceTypeId: string; taskTemplateIds: number[] }) =>
      apiRequest('POST', '/api/service-templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/service-templates'] });
      toast({
        title: "Success",
        description: "Service template created successfully",
      });
      handleClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create service template",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { serviceTypeId: string; taskTemplateIds: number[] }) =>
      apiRequest('PUT', `/api/service-templates/${template?.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/service-templates'] });
      toast({
        title: "Success",
        description: "Service template updated successfully",
      });
      handleClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update service template",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ServiceTemplateFormData) => {
    if (selectedTaskIds.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one task template",
        variant: "destructive",
      });
      return;
    }

    const payload = {
      serviceTypeId: data.serviceTypeId,
      taskTemplateIds: selectedTaskIds,
    };

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleClose = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      form.reset();
      setSelectedTaskIds([]);
      onClose();
    }
  };

  const toggleTaskSelection = (taskId: number) => {
    setSelectedTaskIds(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const removeTask = (taskId: number) => {
    setSelectedTaskIds(prev => prev.filter(id => id !== taskId));
  };

  const handleDragStart = (taskId: number) => {
    setDraggedTaskId(taskId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetTaskId: number) => {
    if (!draggedTaskId || draggedTaskId === targetTaskId) {
      setDraggedTaskId(null);
      return;
    }

    const newOrder = [...selectedTaskIds];
    const draggedIndex = newOrder.indexOf(draggedTaskId);
    const targetIndex = newOrder.indexOf(targetTaskId);

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedTaskId);

    setSelectedTaskIds(newOrder);
    setDraggedTaskId(null);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  const availableServiceTypes = isEditing
    ? serviceTypes.filter(st => st.id === template?.serviceTypeId)
    : serviceTypes.filter(st => !existingTemplates.some(t => t.serviceTypeId === st.id));

  const selectedTasks = selectedTaskIds
    .map(id => taskTemplates.find(t => t.id === id))
    .filter(Boolean) as TaskTemplate[];

  // Fuzzy filter available tasks based on search query
  const filteredTaskTemplates = useMemo(() => {
    if (!searchQuery.trim()) return taskTemplates;
    
    const query = searchQuery.toLowerCase();
    return taskTemplates.filter(task => 
      task.taskName.toLowerCase().includes(query) ||
      task.taskDescription?.toLowerCase().includes(query) ||
      task.formName?.toLowerCase().includes(query) ||
      task.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  }, [taskTemplates, searchQuery]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit' : 'Create'} Service Task List</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="serviceTypeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service *</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    value={field.value}
                    disabled={isEditing}
                  >
                    <FormControl>
                      <SelectTrigger data-testid="select-service-type">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableServiceTypes.map((serviceType) => (
                        <SelectItem key={serviceType.id} value={serviceType.id}>
                          {serviceType.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-3">Selected Tasks (in order)</h4>
                {selectedTasks.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No tasks selected</p>
                ) : (
                  <div className="space-y-2">
                    {selectedTasks.map((task, index) => (
                      <div
                        key={task.id}
                        draggable
                        onDragStart={() => handleDragStart(task.id)}
                        onDragOver={handleDragOver}
                        onDrop={() => handleDrop(task.id)}
                        className="flex items-start gap-3 p-3 bg-muted/50 rounded-md cursor-move hover:bg-muted transition-colors"
                        data-testid={`selected-task-${task.id}`}
                      >
                        <GripVertical className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <span className="text-sm font-medium text-muted-foreground w-4">{index + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-sm">{task.taskName}</p>
                            <Badge variant="secondary" className="text-xs">
                              {task.targetDuration}
                            </Badge>
                          </div>
                          {task.taskDescription && (
                            <p className="text-sm text-muted-foreground mb-2">
                              {task.taskDescription}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 items-center">
                            {task.formName && (
                              <Badge variant="outline" className="text-xs">
                                Form: {task.formName}
                              </Badge>
                            )}
                            {task.url && (
                              <a 
                                href={task.url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`link-selected-task-url-${task.id}`}
                              >
                                <ExternalLink className="h-3 w-3" />
                                {task.url.length > 30 ? task.url.substring(0, 30) + '...' : task.url}
                              </a>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTask(task.id)}
                          data-testid={`button-remove-${task.id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium">Available Task Templates</h4>
                  <div className="flex items-center gap-3">
                    <p className="text-xs text-muted-foreground">
                      {filteredTaskTemplates.length} of {taskTemplates.length} tasks
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCreateTaskModal(true)}
                      data-testid="button-create-new-task"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Create New Task
                    </Button>
                  </div>
                </div>

                <Alert className="mb-3">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    Don't see a task you need? Use the "Create New Task" button above to add it, or visit the{" "}
                    <a 
                      href="/task-templates" 
                      target="_blank" 
                      className="text-primary underline hover:no-underline"
                    >
                      Task Templates
                    </a>{" "}
                    page for full task management.
                  </AlertDescription>
                </Alert>
                
                {/* Search bar */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tasks by name, description, form, or tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="input-search-tasks"
                  />
                </div>

                <div className="max-h-64 overflow-y-auto space-y-2 border rounded-md p-3">
                  {filteredTaskTemplates.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">
                      {searchQuery ? "No tasks match your search" : "No task templates available"}
                    </p>
                  ) : (
                    filteredTaskTemplates.map((task) => (
                      <label
                        key={task.id}
                        className="flex items-start gap-3 p-2 hover:bg-muted/50 rounded-md cursor-pointer transition-colors"
                        data-testid={`available-task-${task.id}`}
                      >
                        <Checkbox
                          checked={selectedTaskIds.includes(task.id)}
                          onCheckedChange={() => toggleTaskSelection(task.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-sm">{task.taskName}</p>
                            <Badge variant="secondary" className="text-xs">
                              {task.targetDuration}
                            </Badge>
                          </div>
                          {task.taskDescription && (
                            <p className="text-sm text-muted-foreground mb-2">
                              {task.taskDescription}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 items-center">
                            {task.formName && (
                              <Badge variant="outline" className="text-xs">
                                Form: {task.formName}
                              </Badge>
                            )}
                            {task.url && (
                              <a 
                                href={task.url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`link-task-url-${task.id}`}
                              >
                                <ExternalLink className="h-3 w-3" />
                                {task.url.length > 30 ? task.url.substring(0, 30) + '...' : task.url}
                              </a>
                            )}
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending} data-testid="button-submit">
                {isPending ? (isEditing ? "Updating..." : "Creating...") : (isEditing ? "Update" : "Create")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>

      <TaskTemplateFormModal 
        open={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
      />
    </Dialog>
  );
}
